import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addVehicleExpense,
  deleteVehicleExpense,
  getVehicleExpenses,
  updateVehicleExpense,
} from "../network/api/vehicleExpenses.api";
import { VehicleExpenseInput } from "../types";

const useInvalidateVehicleExpenses = (vehicleId: string) => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ["vehicleExpenses", vehicleId] });
    queryClient.invalidateQueries({ queryKey: ["vehicles"] });
    queryClient.invalidateQueries({ queryKey: ["salesReport"] });
  };
};

export const useVehicleExpenses = (vehicleId: string, enabled = true) =>
  useQuery({
    queryKey: ["vehicleExpenses", vehicleId],
    queryFn: () => getVehicleExpenses(vehicleId),
    enabled: enabled && !!vehicleId,
    meta: {
      errorMessage: "Failed to load vehicle expenses. Please try again.",
    },
  });

export const useAddVehicleExpense = (vehicleId: string) => {
  const invalidate = useInvalidateVehicleExpenses(vehicleId);

  return useMutation({
    mutationFn: (params: VehicleExpenseInput) =>
      addVehicleExpense({ ...params, vehicle_id: vehicleId }),
    onSuccess: invalidate,
  });
};

export const useUpdateVehicleExpense = (vehicleId: string) => {
  const invalidate = useInvalidateVehicleExpenses(vehicleId);

  return useMutation({
    mutationFn: (params: Partial<VehicleExpenseInput> & { id: string }) =>
      updateVehicleExpense(params),
    onSuccess: invalidate,
  });
};

export const useDeleteVehicleExpense = (vehicleId: string) => {
  const invalidate = useInvalidateVehicleExpenses(vehicleId);

  return useMutation({
    mutationFn: (id: string) => deleteVehicleExpense(id),
    onSuccess: invalidate,
  });
};
